import { AppointmentSummary } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { Feedback, FeedbackEntityType } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import {
  Note,
  NoteMetadata,
  NoteStatusStates,
  NoteTypes,
} from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { baseApi, RTKQueryTag } from '../../../data/baseApi'

type GetNotesArgs =
  | {
      episodeId: string
    }
  | {
      providerId: string
      excludedFields: string[]
      note_types: NoteTypes[]
      months: number
    }

type GetAppointmentSummariesArgs = {
  providerId: string
  statusOnly: boolean
  patientId?: string
  episodeId?: string
  appointmentId?: number
}

type CreateNoteArgs = {
  content_meta_data_id: string
  content_meta_data: NoteMetadata
  patient_id: string | null
  provider_id: string
  episode_id: string
  appointment_id: number | null
  next_appointment_id?: number | null
  next_appointment_start_datetime?: string | null
  note_type: NoteTypes
  version_id: number
  body: Note['body']
  status: NoteStatusStates
}

type GetFeedbackArgs = {
  providerId: string
  entityId: string
}

type GetAiCoachFeedbackArgs = {
  providerId: string
  entityType: FeedbackEntityType
}

type CreateFeedbackArgs = {
  feedback: Omit<Feedback, 'entity_type'> & {
    entity_type: FeedbackEntityType | undefined
  }
}

type DeleteFeedbackArgs = {
  feedbackId: string
}

type UpdateFeedbackResponseArgs = {
  feedbackResponseId: string
  answer: string
}

type UpdateNoteArgs = {
  note_id: string
} & CreateNoteArgs

type DeleteNoteArgs = {
  noteId: string
}

const notesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotes: builder.query<Note[], GetNotesArgs>({
      query: (data) => {
        if ('episodeId' in data) {
          return {
            method: 'get',
            url: '/lt/v1/notes',
            params: { episode_id: data.episodeId },
          }
        }

        const params = new URLSearchParams()
        params.append('provider_id', data.providerId)
        params.append('months', data.months.toString())
        for (const field of data.excludedFields) {
          params.append('excludedFields', field)
        }
        for (const noteType of data.note_types) {
          params.append('note_type', noteType)
        }

        return {
          method: 'get',
          url: `/lt/v1/notes`,
          params,
        }
      },
      providesTags: (_result, _error, args) => ('episodeId' in args ? [] : [RTKQueryTag.NOTE, RTKQueryTag.PERSIST]),
    }),
    getNote: builder.query<Note, string>({
      query: (noteId) => ({
        method: 'get',
        url: `/lt/v1/notes/${noteId}`,
      }),
      providesTags: [RTKQueryTag.NOTE],
    }),
    createNote: builder.mutation<Note, CreateNoteArgs>({
      query: (note) => ({
        method: 'post',
        url: '/lt/v1/notes',
        data: note,
      }),
      invalidatesTags: [RTKQueryTag.NOTE],
    }),
    updateNote: builder.mutation<Note, UpdateNoteArgs>({
      query: ({ note_id, ...note }) => ({
        method: 'put',
        url: `/lt/v1/notes/${note_id}`,
        data: note,
      }),
      invalidatesTags: [RTKQueryTag.NOTE],
    }),
    // Backend only allows this for draft Care Referral notes (LCDELIVERY-7933). The generic error
    // alert is suppressed, so callers must handle failures themselves (e.g. an inline error).
    deleteNote: builder.mutation<void, DeleteNoteArgs>({
      query: ({ noteId }) => ({
        method: 'delete',
        url: `/lt/v1/notes/${noteId}`,
        showAlert: false,
      }),
      // Only refetch after a successful delete; a failed one (403/500) leaves the notes as they were.
      invalidatesTags: (_result, error) => (error ? [] : [RTKQueryTag.NOTE]),
    }),
    getAppointmentSummaries: builder.query<AppointmentSummary[], GetAppointmentSummariesArgs>({
      query: ({ providerId, patientId, episodeId, statusOnly, appointmentId }) => ({
        method: 'get',
        url: `/v1/${providerId}/appointments/summaries`,
        params: {
          patient_id: patientId,
          episode_id: episodeId,
          status_only: statusOnly,
          appointment_id: appointmentId,
        },
      }),
      providesTags: [RTKQueryTag.PERSIST],
    }),
    getFeedback: builder.query<Feedback[], GetFeedbackArgs>({
      query: ({ providerId, entityId }) => ({
        method: 'get',
        url: '/lt/v1/feedback',
        params: { lyra_id: providerId, entity_id: entityId },
      }),
      providesTags: [RTKQueryTag.FEEDBACK],
    }),
    createFeedback: builder.mutation<Feedback, CreateFeedbackArgs>({
      query: ({ feedback }) => ({
        method: 'post',
        url: '/lt/v1/feedback',
        data: feedback,
      }),
      invalidatesTags: [RTKQueryTag.FEEDBACK],
    }),
    getAiCoachFeedback: builder.query<Feedback[], GetAiCoachFeedbackArgs>({
      query: ({ providerId, entityType }) => ({
        method: 'get',
        url: '/lt/v1/feedback',
        params: { lyra_id: providerId, entity_type: entityType },
      }),
      providesTags: [RTKQueryTag.FEEDBACK],
    }),
    deleteFeedback: builder.mutation<void, DeleteFeedbackArgs>({
      query: ({ feedbackId }) => ({
        method: 'delete',
        url: `/lt/v1/feedback/${feedbackId}`,
      }),
      invalidatesTags: [RTKQueryTag.FEEDBACK],
    }),
    updateFeedbackResponse: builder.mutation<void, UpdateFeedbackResponseArgs>({
      query: ({ feedbackResponseId, answer }) => ({
        method: 'patch',
        url: `/lt/v1/feedback_response/${feedbackResponseId}`,
        data: { answer },
      }),
      invalidatesTags: [RTKQueryTag.FEEDBACK],
    }),
  }),
})

export const {
  useGetNotesQuery,
  useLazyGetNotesQuery,
  useLazyGetNoteQuery,
  useCreateNoteMutation,
  useGetAppointmentSummariesQuery,
  useGetFeedbackQuery,
  useCreateFeedbackMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useGetAiCoachFeedbackQuery,
  useDeleteFeedbackMutation,
  useUpdateFeedbackResponseMutation,
} = notesApi
export default notesApi
